import React, { createContext, useContext, useState, useEffect } from 'react';
import configService from '../services/configService';
import bookingService from '../services/bookingService';

const DataCacheContext = createContext();

export const DataCacheProvider = ({ children }) => {
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);

  const clearCache = () => {
    // Security: Clear ALL cached data to prevent cross-user data leakage
    setCache({});
    setLoading({});
  };

  useEffect(() => {
    const handleClearCache = () => {
      clearCache();
      setCurrentUserId(null);
    };
    
    const handleAuthChange = (e) => {
      const newUserId = e.detail?.userId;
      if (newUserId !== currentUserId) {
        // Clear ALL cache on user change for security
        setCache({});
        setLoading({});
        setCurrentUserId(newUserId);
      }
    };
    
    window.addEventListener('clearDataCache', handleClearCache);
    window.addEventListener('authUserChanged', handleAuthChange);
    
    return () => {
      window.removeEventListener('clearDataCache', handleClearCache);
      window.removeEventListener('authUserChanged', handleAuthChange);
    };
  }, [currentUserId]);

  useEffect(() => {
    const handleCacheUpdate = (e) => {
      updateCache(e.detail.key, e.detail.data);
    };
    window.addEventListener('cacheUpdate', handleCacheUpdate);
    return () => {
      window.removeEventListener('cacheUpdate', handleCacheUpdate);
    };
  }, []);

  const updateCache = (key, data) => {
    if (!currentUserId) return;
    
    setCache(prev => {
      const userCache = prev[currentUserId] || {};
      
      if (key === 'settings') {
        return {
          ...prev,
          [currentUserId]: {
            ...userCache,
            settings: { ...userCache.settings, ...data }
          }
        };
      }
      return { 
        ...prev, 
        [currentUserId]: { ...userCache, [key]: data } 
      };
    });
  };

  const getUserCache = () => currentUserId ? cache[currentUserId] || {} : {};
  const getUserLoading = () => currentUserId ? loading[currentUserId] || {} : {};

  const prefetchReferrals = async () => {
    if (!currentUserId) return;
    const userCache = getUserCache();
    const userLoading = getUserLoading();
    
    if (userCache.referrals) return;
    if (userLoading.referrals) return;
    
    setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], referrals: true } }));
    
    try {
      const [referralsData, linkData] = await Promise.all([
        configService.getUserReferrals(),
        configService.getReferralLink()
      ]);
      
      const referralsList = referralsData.referrals || referralsData || [];
      const completed = referralsList.filter(r => r.status === 'complete');
      const totalCredits = completed.reduce((sum, r) => sum + (r.credit_amount || 0), 0);
      
      setCache(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], referrals: { 
        referrals: referralsList,
        referralLink: linkData.referral_link || '',
        analytics: {
          totalReferrals: referralsList.length,
          completedReferrals: completed.length,
          conversionRate: referralsList.length > 0 ? (completed.length / referralsList.length * 100) : 0,
          totalCreditsEarned: totalCredits
        }
      }}}));
    } catch (error) {
      console.error('Failed to prefetch referrals:', error);
    } finally {
      setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], referrals: false } }));
    }
  };

  const prefetchPlans = async () => {
    if (!currentUserId) return;
    const userCache = getUserCache();
    const userLoading = getUserLoading();
    
    if (userCache.plans) return;
    if (userLoading.plans) return;
    
    setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], plans: true } }));
    
    try {
      const status = await configService.getSubscriptionStatus();
      
      // Fetch usage data for prepaid plans to get remaining minutes
      if (status?.payment_type === 'prepaid') {
        try {
          const usageData = await configService.getUsageData();
          status.prepaid_balance_minutes = usageData.remaining_minutes;
        } catch (usageError) {
          console.warn('Failed to fetch usage data:', usageError);
          status.prepaid_balance_minutes = 0;
        }
      }
      
      setCache(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], plans: { subscriptionStatus: status } } }));
    } catch (error) {
      // Silently handle auth errors during prefetch - user can still view plans
      if (error.message.includes('Authentication required') || error.message.includes('No valid token found')) {
        console.log('Skipping subscription status prefetch - user not fully authenticated yet');
      } else {
        console.error('Failed to prefetch plans:', error);
      }
    } finally {
      setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], plans: false } }));
    }
  };

  const prefetchSkills = async () => {
    if (!currentUserId) return;
    const userCache = getUserCache();
    const userLoading = getUserLoading();
    
    if (userCache.skills) return;
    if (userLoading.skills) return;
    
    setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], skills: true } }));
    
    try {
      const response = await configService.getModules();
      setCache(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], skills: { modules: response.modules || [] } } }));
    } catch (error) {
      console.error('Failed to prefetch skills:', error);
    } finally {
      setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], skills: false } }));
    }
  };

  const prefetchLogs = async () => {
    if (!currentUserId) return;
    const userCache = getUserCache();
    const userLoading = getUserLoading();
    
    if (userCache.logs) return;
    if (userLoading.logs) return;
    
    setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], logs: true } }));
    
    try {
      const token = await configService.getAuthToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://oauth.remote-assistant.ai';
      
      const [conversations, smsData] = await Promise.all([
        configService.getConversations(100).catch(() => ({ conversations: [], last_key: null })),
        fetch(`${API_BASE_URL}/oauth/conversations/sms`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : { conversations: [] }).catch(() => ({ conversations: [] }))
      ]);

      setCache(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], logs: { conversations: conversations.conversations || [], last_key: conversations.last_key, smsConversations: smsData.conversations || [] } } }));
    } catch (error) {
      console.error('Failed to prefetch logs:', error);
    } finally {
      setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], logs: false } }));
    }
  };

  const prefetchBookings = async () => {
    if (!currentUserId) return;
    const userCache = getUserCache();
    const userLoading = getUserLoading();
    
    if (userCache.bookings) return;
    if (userLoading.bookings) return;
    
    setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], bookings: true } }));
    
    try {
      const [staff, services, bookings] = await Promise.all([
        bookingService.listStaff().catch(() => []),
        bookingService.listServices().catch(() => []),
        bookingService.listBusinessBookings().catch(() => [])
      ]);

      setCache(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], bookings: { staff, services, bookings } } }));
    } catch (error) {
      console.error('Failed to prefetch bookings:', error);
    } finally {
      setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], bookings: false } }));
    }
  };

  const prefetchSettings = async () => {
    if (!currentUserId) return;
    const userCache = getUserCache();
    const userLoading = getUserLoading();
    
    if (userCache.settings) return;
    if (userLoading.settings) return;
    
    setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], settings: true } }));
    
    try {
      const token = await configService.getAuthToken();
      const cognitoUserId = configService.getUserId();
      
      const [businessConfig, modules, voices, routingRules, services] = await Promise.all([
        configService.getBusinessConfigByUserId(),
        configService.getModules(),
        configService.getAvailableVoices(),
        fetch(`https://oauth.remote-assistant.ai/oauth/users/${cognitoUserId}/routing-rules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : { rules: [] }).catch(() => ({ rules: [] })),
        bookingService.listServices().catch(() => [])
      ]);
      
      const moduleMap = {};
      modules.modules.forEach(m => {
        moduleMap[m.module_id] = m.enabled;
      });
      
      setCache(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], settings: { businessConfig, modules: moduleMap, voices: voices.voices, routingRules: routingRules.rules || [], services } } }));
    } catch (error) {
      console.error('Prefetch settings failed:', error);
    } finally {
      setLoading(prev => ({ ...prev, [currentUserId]: { ...prev[currentUserId], settings: false } }));
    }
  };

  return (
    <DataCacheContext.Provider value={{ cache: getUserCache(), prefetchSettings, prefetchBookings, prefetchLogs, prefetchSkills, prefetchPlans, prefetchReferrals, updateCache, clearCache }}>
      {children}
    </DataCacheContext.Provider>
  );
};

export const useDataCache = () => useContext(DataCacheContext);
